import { z } from 'zod';
import { CURRENT_SCHEMA_VERSION } from './constants.js';

export const uuidSchema = z.string().uuid();
export const isoDateSchema = z.string().datetime({ offset: true });

export const rectMmSchema = z.object({
  x: z.number(),
  y: z.number(),
  width: z.number().positive(),
  height: z.number().positive(),
});

export const sizeMmSchema = z.object({
  width: z.number().positive(),
  height: z.number().positive(),
});

export const photoFiltersSchema = z.object({
  brightness: z.number().min(-1).max(1).default(0),
  contrast: z.number().min(-1).max(1).default(0),
  saturation: z.number().min(-1).max(1).default(0),
  warmth: z.number().min(-1).max(1).default(0),
  grayscale: z.boolean().default(false),
});

export type PhotoFilters = z.infer<typeof photoFiltersSchema>;

export const photoPlacementSchema = z.object({
  /** Scale of the image relative to cover-fit scale (1 = cover) */
  scale: z.number().positive().default(1),
  /** Normalized offset of image center within frame (-1..1 typically) */
  offsetX: z.number().default(0),
  offsetY: z.number().default(0),
  rotation: z.number().default(0),
  flipX: z.boolean().default(false),
  flipY: z.boolean().default(false),
});

export type PhotoPlacement = z.infer<typeof photoPlacementSchema>;

const baseElementSchema = z.object({
  id: uuidSchema,
  x: z.number(),
  y: z.number(),
  width: z.number().positive(),
  height: z.number().positive(),
  rotation: z.number().default(0),
  opacity: z.number().min(0).max(1).default(1),
  zIndex: z.number().int(),
  locked: z.boolean().default(false),
  hidden: z.boolean().default(false),
  templateSourceId: z.string().optional(),
});

export const photoFrameElementSchema = baseElementSchema.extend({
  type: z.literal('photoFrame'),
  assetId: z.string().uuid().nullable(),
  placement: photoPlacementSchema.default({}),
  filters: photoFiltersSchema.default({}),
  cornerRadiusMm: z.number().min(0).default(0),
  altText: z.string().max(500).optional(),
  required: z.boolean().default(false),
});

export const textElementSchema = baseElementSchema.extend({
  type: z.literal('text'),
  content: z.string(),
  fontFamily: z.string(),
  fontSizePt: z.number().positive(),
  fontWeight: z
    .union([z.literal(400), z.literal(500), z.literal(600), z.literal(700)])
    .default(400),
  fontStyle: z.enum(['normal', 'italic']).default('normal'),
  lineHeight: z.number().positive().default(1.3),
  letterSpacingEm: z.number().default(0),
  align: z.enum(['left', 'center', 'right', 'justify']).default('left'),
  color: z.string().default('#1a1a1a'),
  backgroundColor: z.string().nullable().default(null),
  autoHeight: z.boolean().default(true),
  verticalAlign: z.enum(['top', 'middle', 'bottom']).default('top'),
});

export const shapeElementSchema = baseElementSchema.extend({
  type: z.literal('shape'),
  shape: z.enum(['rectangle', 'ellipse', 'line']),
  fill: z.string().nullable().default('#e8e4de'),
  stroke: z.string().nullable().default(null),
  strokeWidthMm: z.number().min(0).default(0),
  cornerRadiusMm: z.number().min(0).default(0),
});

export const decorationElementSchema = baseElementSchema.extend({
  type: z.literal('decoration'),
  decorationKind: z.enum(['divider', 'ornament', 'frame']),
  color: z.string().default('#1a1a1a'),
});

export const pageElementSchema = z.discriminatedUnion('type', [
  photoFrameElementSchema,
  textElementSchema,
  shapeElementSchema,
  decorationElementSchema,
]);

export type PageElement = z.infer<typeof pageElementSchema>;
export type PhotoFrameElement = z.infer<typeof photoFrameElementSchema>;
export type TextElement = z.infer<typeof textElementSchema>;
export type ShapeElement = z.infer<typeof shapeElementSchema>;

export const pageBackgroundSchema = z.object({
  type: z.enum(['color', 'none']),
  color: z.string().default('#ffffff'),
});

export const pageDocumentSchema = z.object({
  id: uuidSchema,
  schemaVersion: z.literal(CURRENT_SCHEMA_VERSION).or(z.number().int().positive()),
  order: z.number().int().nonnegative(),
  background: pageBackgroundSchema.default({ type: 'color', color: '#ffffff' }),
  elements: z.array(pageElementSchema).default([]),
  templateId: z.string().optional(),
  warnings: z.array(z.string()).default([]),
});

export type PageDocument = z.infer<typeof pageDocumentSchema>;

export const coverPanelSchema = z.object({
  id: uuidSchema,
  panel: z.enum(['front', 'back', 'spine']),
  background: pageBackgroundSchema.default({ type: 'color', color: '#ffffff' }),
  elements: z.array(pageElementSchema).default([]),
  templateId: z.string().optional(),
});

export const coverDocumentSchema = z.object({
  schemaVersion: z.literal(CURRENT_SCHEMA_VERSION).or(z.number().int().positive()),
  panels: z.object({
    front: coverPanelSchema,
    back: coverPanelSchema,
    spine: coverPanelSchema,
  }),
});

export type CoverDocument = z.infer<typeof coverDocumentSchema>;

export const assetReferenceSchema = z.object({
  id: uuidSchema,
  originalFileName: z.string(),
  mimeType: z.enum(['image/jpeg', 'image/png', 'image/webp', 'image/heic', 'image/heif']),
  byteSize: z.number().int().nonnegative(),
  widthPx: z.number().int().positive(),
  heightPx: z.number().int().positive(),
  exifOrientation: z.number().int().min(1).max(8).default(1),
  importedAt: isoDateSchema,
  checksumSha256: z.string().length(64),
  localLocator: z.string().optional(),
  /** Server-side asset row id (see apps/api assets table) once uploaded — required to re-fetch presigned URLs for this asset. */
  cloudAssetId: z.string().optional(),
  cloudObjectKey: z.string().optional(),
  thumbnailLocator: z.string().optional(),
  previewLocator: z.string().optional(),
  syncStatus: z.enum(['local-only', 'pending-upload', 'uploaded', 'error']).default('local-only'),
  capturedAt: isoDateSchema.optional(),
  useCount: z.number().int().nonnegative().default(0),
});

export type AssetReference = z.infer<typeof assetReferenceSchema>;

export const projectSettingsSchema = z.object({
  showBleedGuides: z.boolean().default(true),
  showSafeZoneGuides: z.boolean().default(true),
  showTrimGuides: z.boolean().default(true),
  showGutterGuides: z.boolean().default(true),
  snapEnabled: z.boolean().default(true),
  allowLowDpiLocalExport: z.boolean().default(false),
  paperStockId: z.string().default('standard-matte'),
});

export type ProjectSettings = z.infer<typeof projectSettingsSchema>;

export const photobookProjectSchema = z.object({
  schemaVersion: z.number().int().positive(),
  id: uuidSchema,
  ownerMode: z.enum(['local', 'cloud']),
  ownerId: z.string().optional(),
  cloudProjectId: z.string().uuid().optional(),
  title: z.string().min(1).max(200),
  description: z.string().max(2000).optional(),
  productProfileId: z.string(),
  createdAt: isoDateSchema,
  updatedAt: isoDateSchema,
  revision: z.number().int().nonnegative(),
  locale: z.literal('nl-NL').default('nl-NL'),
  cover: coverDocumentSchema,
  pages: z.array(pageDocumentSchema).min(1),
  assets: z.array(assetReferenceSchema).default([]),
  settings: projectSettingsSchema.default({}),
});

export type PhotobookProject = z.infer<typeof photobookProjectSchema>;

export const spineFormulaSchema = z.object({
  id: z.string(),
  label: z.string(),
  /** Fictional demo formula — NOT suitable for real print providers */
  disclaimer: z.string(),
  /** spineMm = baseMm + pages * mmPerPage * paperFactor */
  baseMm: z.number(),
  mmPerPage: z.number(),
  paperFactors: z.record(z.string(), z.number()),
});

export const productProfileSchema = z.object({
  schemaVersion: z.literal(CURRENT_SCHEMA_VERSION),
  id: z.string(),
  name: z.string(),
  description: z.string(),
  pageSizeMm: sizeMmSchema,
  bleedMm: z.number().nonnegative(),
  safeMarginMm: z.number().nonnegative(),
  pageCount: z.object({
    min: z.number().int().positive(),
    max: z.number().int().positive(),
    default: z.number().int().positive(),
    step: z.number().int().positive(),
  }),
  paperStocks: z.array(
    z.object({
      id: z.string(),
      name: z.string(),
      description: z.string(),
    }),
  ),
  spineFormula: spineFormulaSchema,
  targetDpi: z.number().positive(),
  warnDpi: z.number().positive(),
  minDpi: z.number().positive(),
  colorProfile: z.enum(['sRGB']),
  cover: z.object({
    hasFront: z.literal(true),
    hasBack: z.literal(true),
    hasSpine: z.literal(true),
    wrapBleedMm: z.number().nonnegative(),
  }),
  export: z.object({
    defaultDpi: z.number().positive(),
    jpegQuality: z.number().min(0.1).max(1),
    includeBleed: z.boolean(),
  }),
});

export type ProductProfile = z.infer<typeof productProfileSchema>;

export const printProviderProfileSchema = z.object({
  schemaVersion: z.literal(CURRENT_SCHEMA_VERSION),
  id: z.string(),
  name: z.string(),
  /** Adapter hook for future print partners — not wired to UI ordering in v1 */
  status: z.enum(['stub', 'active']),
  productProfileId: z.string(),
  iccProfileHint: z.string().optional(),
  pdfProfileHint: z.string().optional(),
  spineFormulaOverrideId: z.string().optional(),
});

export type PrintProviderProfile = z.infer<typeof printProviderProfileSchema>;
